<template>
  <v-container>
    <h1>Mise à jour de la base de données</h1>
    <p>
      Cette page permet de mettre à jour la base de données avec les nouvelles informations disponibles sur le site Club40.
      Attention de bien controler que les informations sont cohérentes avant de les envoyer sur le serveur.
    </p>
    <v-stepper v-model="step" vertical>
      <v-stepper-step :complete="step > 1" step="1">Authentification</v-stepper-step>
      <v-stepper-content step="1">
        <p>
          Authentifiez-vous afin de pouvoir commencer la récupération les données de Club40.
        </p>
        <v-alert type="error" :value="!!loginMessage">
          {{ loginMessage }}
        </v-alert>
        <v-row>
          <v-col
            cols="12"
            md="4"
          >
            <v-text-field
              v-model="email"
              label="E-mail"
              required
            ></v-text-field>
          </v-col>
          <v-col
            cols="12"
            md="4"
          >
            <v-text-field
              type="password"
              v-model="password"
              label="********"
              required
            ></v-text-field>
          </v-col>
          </v-row>
        <v-btn color="primary" @click="login" :loading="loading === 1">Se connecter</v-btn>
      </v-stepper-content>

      <v-stepper-step :complete="step > 2" step="2">Nouveaux classements disponibles</v-stepper-step>
      <v-stepper-content step="2">
        <p>
          Les caractéristiques actuelles de la base de données sont :
        </p>
          <ul class="mb-4">
          <li>Nombre de classements : {{ datesCount }}</li>
          <li>Premier classement : {{ firstDate.toLocaleDateString() }}</li>
          <li>Dernier classement : {{ lastDate.toLocaleDateString() }}</li>
        </ul>
        <p v-if="availableDates.length > 0">
          Liste des nouveaux classements disponibles sur Club40 est la suivante.
        </p>
        <v-progress-circular
          v-if="loading === 2"
          indeterminate
          color="primary"
        ></v-progress-circular>
        <v-chip v-else-if="availableDates.length > 0" class="ma-2" v-for="(date, key) in availableDates" :key="key" @click="getRanking(date)">
          {{ date.text }}
          <v-icon right>mdi-check</v-icon>
        </v-chip>
        <v-alert v-else type="info">
          Il ne semble pas y avoir de nouveaux classements disponibles actuellement.
        </v-alert>
        <p class="mt-4" v-if="availableDates.length > 0">
          Cliquez sur une date pour passer à l'étape suivante et ainsi récupérer le contenu.
        </p>
      </v-stepper-content>

      <v-stepper-step :complete="step > 3" step="3">Contenu du classement</v-stepper-step>
      <v-stepper-content step="3">
        <p>
          Veuillez vérifier le contenu du classement, que les données soient cohérentes et qu'il existe bien 40 lignes, avant de valider le contenu.
        </p>
        <ul class="mb-4">
          <li>Classement en date du {{ rankingDate.toLocaleDateString() }}.</li>
          <li>Extrait de la page <a :href="`http://www.club40.fr/${rankingPage}`" target="_blank">{{ `http://www.club40.fr/${rankingPage}` }}</a>.</li>
          <li>{{ ranking.length }} lignes de classement trouvées.</li>
        </ul>
        <v-container fluid>
          <v-data-table
            :loading="loading === 3"
            :headers="headers"
            :items="ranking"
            :items-per-page="10"
            class="elevation-1"
          ></v-data-table>
        </v-container>
        <v-btn class="mr-4" color="primary" @click="step = 4">Je valide le contenu</v-btn>
        <v-btn @click="step = 2">Retour</v-btn>
      </v-stepper-content>

      <v-stepper-step step="4">Ajout à la base de données</v-stepper-step>
      <v-stepper-content step="4">
        <p>
          Pour sauvegarder le classement dans la base de données, cliquez sur Envoyer ? Attention, cette action est irréversible.
        </p>
        <v-progress-linear
          class="mb-4"
          :value="rankingSent / rankingCount * 100"
          color="primary"
          height="25"
          reactive
          striped
        >
          <strong>{{ rankingSent }} sur {{ rankingCount }}</strong>
        </v-progress-linear>
        <p v-if="updateResponse !== ''">
          <code>{{ updateResponse }}</code>
        </p>
        <v-btn v-if="updateResponse === ''" class="mr-4" color="primary" @click="sendRanking()" :loading="loading === 4">Envoyer</v-btn>
        <v-btn v-if="updateResponse === ''" class="mr-4" @click="step = 3">Retour</v-btn>
        <v-btn v-else color="warning" @click="step = 2; updateResponse = ''">Recommencer</v-btn>
      </v-stepper-content>
    </v-stepper>
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import cheerio from 'cheerio'
import axios from 'axios'

export default {
  data: () => ({
    step: 1,
    availableDates: [],
    rankingDate: new Date(),
    rankingPage: null,
    ranking: [],
    rankingCount: 1,
    headers: [
      { text: 'Classement', value: 'ranking' },
      { text: 'Progression', value: 'progress' },
      { text: 'Titre', align: 'start', value: 'title' },
      { text: 'Artiste', align: 'start', value: 'artist' },
      { text: 'BPM', value: 'bpm' }
    ],
    loading: 0,
    updateResponse: '',
    email: '',
    password: '',
    loginMessage: null
  }),
  computed: {
    ...mapGetters(['firstDate', 'lastDate', 'allDatesString', 'datesCount']),
    rankingSent () {
      return this.rankingCount - this.ranking.length
    }
  },
  watch: {
    step () {
      if (this.step === 2) {
        this.loading = 2
        this.availableDates = []
        this.updateResponse = ''
        this.getListWeeks()
          .then(listWeeks => {
            this.availableDates = listWeeks.filter(item => !this.allDatesString.includes(item.date))
            this.loading = 0
          })
      }
    }
  },
  created () {
    axios.get(process.env.VUE_APP_BACKEND_LOCATION + '/me', { withCredentials: true })
      .then(response => {
        if (response.status === 200) {
          this.loggedIn = true
          this.step = 2
        }
      })
      .catch(error => {
        console.error(error)
      })
  },
  methods: {
    ...mapActions(['loadDates']),
    login () {
      this.loginMessage = null
      this.loading = 1
      axios.post(
        process.env.VUE_APP_BACKEND_LOCATION + '/login',
        { email: this.email, password: this.password },
        { withCredentials: true }
      )
        .then(response => {
          if (response.status === 204) {
            this.step = 2
          } else {
            this.loginMessage = response.error
          }
          this.loading = 0
        })
        .catch(error => {
          this.loading = 0
          this.loginMessage = error.message
        })
    },
    getListWeeks () {
      return axios.get(process.env.VUE_APP_BACKEND_LOCATION + '/proxy/dates', { withCredentials: true })
        .then(response => {
          const listWeeks = []
          const $ = cheerio.load(response.data, { decodeEntities: false })
          $('#sel_we').find('option').each((index, item) => {
            const textDate = $(item).text()
            listWeeks.push({
              page: $(item).val(),
              text: textDate,
              date: textDate.substring(6) + '-' + textDate.substring(3, 5) + '-' + textDate.substring(0, 2)
            })
          })
          return listWeeks
        })
        .catch(error => {
          console.error(error)
        })
    },
    getRanking (date) {
      this.loading = 3
      this.step = 3
      this.rankingDate = new Date(date.date)
      this.rankingPage = date.page
      return axios.get(
        process.env.VUE_APP_BACKEND_LOCATION + '/proxy/ranking',
        {
          params: { page: date.page },
          withCredentials: true
        }
      )
        .then(response => {
          const $ = cheerio.load(response.data, { decodeEntities: false })
          const rankingTable = []
          $('table table').find('tr').each((index, item) => {
            if ($(item).children().first().attr('width') === '100') {
              rankingTable.push({
                date: this.rankingDate,
                ranking: parseInt($(item).children().eq(1).text()),
                progress: isNaN($(item).children().eq(3).text()) ? null : parseInt($(item).children().eq(3).text()),
                artist: $(item).children().eq(5).children().children().html().split('<br>')[0],
                title: $(item).children().eq(5).children().children().html().split('<br>')[1],
                bpm: isNaN($(item).children().eq(8).text()) ? 0 : parseInt($(item).children().eq(8).text())
              })
            }
          })
          this.ranking = rankingTable
          this.rankingCount = this.ranking.length
          this.loading = 0
        })
        .catch(error => {
          this.loading = 0
          console.error(error)
        })
    },
    async sendRanking () {
      this.loading = 4
      this.updateResponse = ''
      while (this.ranking.length > 0) {
        const result = await axios.post(
          process.env.VUE_APP_BACKEND_LOCATION + '/song_rankings',
          this.ranking.pop(),
          {
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json'
            },
            withCredentials: true
          }
        )
          .then(response => {
            if (response.code !== 200) {
              this.updateResponse = response.data
              return true
            }
          })
          .catch(error => {
            this.updateResponse = error.message
            return false
          })
        if (!result) {
          break
        }
      }
      this.loadDates()
      this.loading = 0
    }
  }
}
</script>
